'use client';

import React, { createContext, useContext, useEffect, useState, useCallback, useRef } from 'react';
import { User } from '@supabase/supabase-js';
import { useToast } from '@/hooks/useToast';
import { createClient } from '@/lib/supabase/browser';
import { useAuthContext } from '@/components/providers/AuthProvider';

interface UserProfile {
  id: string;
  email: string;
  username?: string;
  display_name?: string;
  full_name?: string;
  preferred_name?: string;
  avatar_url?: string;
  role: string;
  account_type: string;
  onboarding_completed: boolean;
  is_admin?: boolean;
  is_reviewer?: boolean;
  created_at: string;
  updated_at: string;
}

interface UserContextType {
  userData: {
    user: User | null;
    profile: UserProfile | null;
  } | null;
  isLoading: boolean;
  error: string | null;
  refreshProfile: () => Promise<void>;
  updateProfile: (updates: Partial<UserProfile>) => Promise<void>;
}

const UserContext = createContext<UserContextType | undefined>(undefined);

export function UserProvider({ children }: { children: React.ReactNode }) {
  const { toast } = useToast();
  // Consume the session AuthProvider already resolved (UserProvider is mounted
  // inside AuthProvider). Avoids a redundant getSession() round-trip on every
  // page — the previous mount effect called supabase.auth.getSession() itself.
  const { user: authUser, loading: authLoading } = useAuthContext();
  const [userData, setUserData] = useState<{
    user: User | null;
    profile: UserProfile | null;
  } | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const ensuring = useRef(false); // Prevent duplicate ensure calls
  const profileCacheRef = useRef<{ userId: string; profile: UserProfile; timestamp: number } | null>(null);
  const CACHE_DURATION_MS = 30000; // Cache for 30 seconds
  const PROFILE_SELECT_FIELDS =
    'id, email, username, display_name, full_name, preferred_name, avatar_url, role, account_type, onboarding_completed, is_admin, is_reviewer, created_at, updated_at';

  // Ensure profile exists using the server API route (authenticated via cookies)
  const ensureProfileExists = useCallback(async (user: User) => {
    if (ensuring.current) return; // Prevent duplicate calls
    ensuring.current = true;
    
    try {
      console.info('[UserProvider] ensureProfileExists → start', { 
        id: user.id, 
        email: user.email,
        metadata: user.user_metadata 
      });

      // Call the server API route instead of client-side upsert
      const response = await fetch('/api/profile/ensure', {
        method: 'POST',
        credentials: 'include', // Include cookies for authentication
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(`Failed to ensure profile: ${errorData.error || response.statusText}`);
      }

      const result = await response.json();
      console.info('[UserProvider] ensureProfileExists → success via API', result);
      return result;
    } catch (error) {
      console.error('[UserProvider] ensureProfileExists → error', error);
      throw error;
    } finally {
      ensuring.current = false;
    }
  }, []);

  // Load user profile data with caching
  const loadUserProfile = useCallback(async (user: User) => {
    try {
      setIsLoading(true);
      setError(null);

      // Check cache first
      if (profileCacheRef.current && 
          profileCacheRef.current.userId === user.id &&
          Date.now() - profileCacheRef.current.timestamp < CACHE_DURATION_MS) {
        console.info('[UserProvider] loadUserProfile → using cache', { userId: user.id });
        setUserData({
          user,
          profile: profileCacheRef.current.profile
        });
        setIsLoading(false);
        return;
      }

      // Tolerant read - don't fail if profile doesn't exist yet
      const supabase = createClient();
      const { data: profile, error } = await supabase
        .from('profiles')
        .select(PROFILE_SELECT_FIELDS)
        .eq('id', user.id)
        .maybeSingle();

      if (error) {
        // Check if it's a 401/406 error (user doesn't exist or unauthorized)
        // Supabase errors can have code (like 'PGRST301') or status codes in message
        const errorCode = (error as any).code || '';
        const errorMessage = error.message || '';
        const errorStatus = (error as any).status || '';
        
        const isUnauthorized = 
          errorCode === 'PGRST301' || // Not Acceptable
          errorStatus === 406 || 
          errorStatus === 401 ||
          errorMessage.includes('406') || 
          errorMessage.includes('401') || 
          errorMessage.includes('not found') ||
          errorMessage.includes('does not exist');
        
        if (isUnauthorized) {
          console.warn('⚠️ Profile fetch returned 401/406 - user may have been deleted, clearing session...', { errorCode, errorStatus, errorMessage });
          // Clear the session and redirect to login
          await supabase.auth.signOut();
          if (typeof window !== 'undefined') {
            window.location.href = '/login';
          }
          return;
        }
        // log but don't crash the UI; allow onboarding flow to create it
        console.error('[loadUserProfile] error', error);
      }

      if (!profile) {
        // ask the server to ensure it (this call now carries cookies thanks to middleware)
        const resp = await fetch('/api/profile/ensure', { method: 'POST' });
        if (!resp.ok) {
          const j = await resp.json().catch(() => ({}));
          console.error('[ensureProfileExists] upsert error', j);
          
          // If 401, user doesn't exist - clear session and redirect
          if (resp.status === 401) {
            console.warn('⚠️ Profile ensure returned 401 - user may have been deleted, clearing session...');
            const supabase = createClient();
            await supabase.auth.signOut();
            if (typeof window !== 'undefined') {
              window.location.href = '/login';
            }
            return;
          }
          // Don't throw for other errors - just log
        } else {
          // Profile was created, try to fetch it again
          const { data: newProfile, error: retryError } = await supabase
            .from('profiles')
            .select(PROFILE_SELECT_FIELDS)
            .eq('id', user.id)
            .maybeSingle();

          if (!retryError && newProfile) {
            setUserData({
              user,
              profile: newProfile as UserProfile
            });
            console.info('[UserProvider] loadUserProfile → profile created and loaded', { profile: newProfile });
            return;
          }
        }
      } else {
        // Profile exists, set the data and cache it
        const profileData = profile as UserProfile;
        setUserData({
          user,
          profile: profileData
        });
        // Update cache
        profileCacheRef.current = {
          userId: user.id,
          profile: profileData,
          timestamp: Date.now()
        };
        console.info('[UserProvider] loadUserProfile → success', { profile });
      }

    } catch (error) {
      console.error('[UserProvider] loadUserProfile → error', error);
      setError(error instanceof Error ? error.message : 'Failed to load user data');
      
      toast({
        title: 'Profile Error',
        description: 'Failed to load your profile. Please try again.',
        variant: 'destructive',
      });
    } finally {
      setIsLoading(false);
    }
  }, [toast]);

  // Refresh profile data (clears cache)
  const refreshProfile = useCallback(async () => {
    if (userData?.user) {
      profileCacheRef.current = null; // Clear cache
      await loadUserProfile(userData.user);
    }
  }, [userData?.user, loadUserProfile]);
  
  // Clear cache when user changes
  useEffect(() => {
    if (!userData?.user) {
      profileCacheRef.current = null;
    }
  }, [userData?.user]);

  // Update profile
  const updateProfile = useCallback(async (updates: Partial<UserProfile>) => {
    if (!userData?.user || !userData?.profile) return;

    try {
      const supabase = createClient();
      
      const { data: updatedProfileData, error: updateError } = await supabase
        .from('profiles')
        .update({ ...updates, updated_at: new Date().toISOString() })
        .eq('id', userData.user.id)
        .select(PROFILE_SELECT_FIELDS)
        .maybeSingle();

      if (updateError) {
        throw new Error(`Failed to update profile: ${updateError.message}`);
      }

      if (!updatedProfileData) {
        throw new Error('Profile not found after update');
      }

      setUserData(prev => prev ? {
        ...prev,
        profile: updatedProfileData as UserProfile
      } : null);

      toast({
        title: 'Profile Updated',
        description: 'Your profile has been updated successfully.',
      });

    } catch (error) {
      console.error('[UserProvider] updateProfile → error', error);
      toast({
        title: 'Update Failed',
        description: 'Failed to update your profile. Please try again.',
        variant: 'destructive',
      });
      throw error;
    }
  }, [userData?.user, userData?.profile, toast]);

  // Load profile from AuthProvider's session (no independent getSession). Waits
  // for AuthProvider to finish resolving, then loads the profile for the signed-in
  // user or clears state when signed out. The 30s profile cache in
  // loadUserProfile absorbs any auth-state churn (e.g. token refresh).
  useEffect(() => {
    let cancelled = false;

    if (authLoading) return; // AuthProvider hasn't resolved the session yet

    if (authUser) {
      if (!cancelled) void loadUserProfile(authUser);
    } else {
      if (!cancelled) {
        setUserData(null);
        setIsLoading(false);
      }
    }

    return () => { cancelled = true; };
  }, [authUser, authLoading, loadUserProfile]);

  const value: UserContextType = {
    userData,
    isLoading,
    error,
    refreshProfile,
    updateProfile,
  };

  return (
    <UserContext.Provider value={value}>
      {children}
    </UserContext.Provider>
  );
}

export function useUserContext() {
  const context = useContext(UserContext);
  if (context === undefined) {
    throw new Error('useUserContext must be used within a UserProvider');
  }
  return context;
}

export function useEnsureProfile() {
  const [ready, setReady] = useState(false);

  useEffect(() => {
    let cancelled = false;

    async function init() {
      const supabase = createClient();
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.user?.id) return; // not signed in yet

      // Ensure profile exists via server API instead of client-side upsert
      try {
        const response = await fetch('/api/profile/ensure', {
          method: 'POST',
          credentials: 'include',
        });

        if (!response.ok) {
          const errorData = await response.json().catch(() => ({}));
          console.error("[ensureProfile] API error:", errorData);
          return;
        }

        if (!cancelled) setReady(true);
      } catch (error) {
        console.error("[ensureProfile] error:", error);
      }
    }

    init();
    return () => { cancelled = true; };
  }, []);

  return ready;
} 